import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { FaHeart, FaComment, FaShare, FaPlay, FaPause, FaVolumeMute, FaVolumeUp, FaArrowLeft, FaMusic, FaEllipsisH, FaBookmark, FaTrash } from "react-icons/fa";
import { FiHeart, FiMessageCircle, FiSend, FiMoreVertical } from "react-icons/fi";
import { MdClose, MdPause } from "react-icons/md";
import { BiHeart, BiShareAlt } from "react-icons/bi";

function InstagramReels() {
  const { image, type } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const initialPromotionId = image;
  const dealerId = localStorage.getItem("dealer_id");
  const server_url = localStorage.getItem("server_url");
  const loginId = dealerId;

  // States
  const [reels, setReels] = useState([]);
  const [currentReelIndex, setCurrentReelIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [isMuted, setIsMuted] = useState(true); // Start muted like Instagram
  const [showMuteToast, setShowMuteToast] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [selectedReelId, setSelectedReelId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showShareSheet, setShowShareSheet] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isLiking, setIsLiking] = useState(false);
  const [likedReels, setLikedReels] = useState(new Set());

  // Refs
  const scrollContainerRef = useRef(null);
  const videoRefs = useRef([]);
  const isFetching = useRef(false);
  const oldReelIds = useRef("");
  const progressInterval = useRef(null);
  const touchStartY = useRef(0);
  const touchStartX = useRef(0);
  const lastTapTime = useRef(0);
  const muteToastTimeout = useRef(null);
  const longPressTimeout = useRef(null);
  const isScrolling = useRef(false);

  // Fetch reels
  const fetchReels = useCallback(async (oldIds = "") => {
    if (isFetching.current) return;
    isFetching.current = true;
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("company_id", "2");
      formData.append("dealer_id", dealerId);
      formData.append("promotion_id", initialPromotionId);
      formData.append("promotion_type", type);
      formData.append("old_promotion_id", oldIds);

      const res = await axios.post(`${server_url}promotion_scroll.php`, formData);
      
      if (res.data.status === "1") {
        const newReels = res.data.result;
        if (newReels.length > 0) {
          setReels(prev => {
            const existingIds = new Set(prev.map(r => r.promotion_id));
            const filteredNew = newReels.filter(r => !existingIds.has(r.promotion_id));
            return [...prev, ...filteredNew];
          });
          
          const newIds = newReels.map(r => r.promotion_id).join(",");
          oldReelIds.current = oldReelIds.current ? `${oldReelIds.current},${newIds}` : newIds;
        }
      }
    } catch (error) {
      console.error("Failed to fetch reels:", error);
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  }, [server_url, dealerId, initialPromotionId, type]);

  // Initialize
  useEffect(() => {
    fetchReels("");
  }, [fetchReels]);

  // Handle scroll to change reels
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current || isScrolling.current) return;

      const scrollTop = scrollContainerRef.current.scrollTop;
      const windowHeight = window.innerHeight;
      const newIndex = Math.round(scrollTop / windowHeight);

      if (newIndex !== currentReelIndex) {
        // Pause previous video
        if (videoRefs.current[currentReelIndex]) {
          videoRefs.current[currentReelIndex].pause();
        }

        setCurrentReelIndex(newIndex);
        
        // Load more if near end
        if (newIndex + 3 >= reels.length && reels.length > 0) {
          const nextOldIds = reels.slice(0, newIndex + 1).map(r => r.promotion_id).join(",");
          fetchReels(nextOldIds);
        }
      }
    };

    const scrollContainer = scrollContainerRef.current;
    scrollContainer?.addEventListener("scroll", handleScroll, { passive: true });
    
    return () => scrollContainer?.removeEventListener("scroll", handleScroll);
  }, [currentReelIndex, reels, fetchReels]);

  // Handle video playback
  useEffect(() => {
    // Clear all intervals
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
    }

    // Play current video
    if (reels.length > 0 && videoRefs.current[currentReelIndex]) {
      const currentVideo = videoRefs.current[currentReelIndex];
      
      // Reset progress
      setProgress(0);
      
      // Start playing
      currentVideo.muted = isMuted;
      currentVideo.currentTime = 0;
      
      const playPromise = currentVideo.play();
      
      if (playPromise !== undefined) {
        playPromise.then(() => {
          setIsPlaying(true);
          
          // Start progress tracking
          progressInterval.current = setInterval(() => {
            if (currentVideo && !currentVideo.paused) {
              const newProgress = (currentVideo.currentTime / currentVideo.duration) * 100;
              setProgress(newProgress);
              
              if (newProgress >= 100) {
                clearInterval(progressInterval.current);
              }
            }
          }, 100);
        }).catch(() => {
          setIsPlaying(false);
        });
      }
    }

    // Pause all other videos
    videoRefs.current.forEach((video, index) => {
      if (video && index !== currentReelIndex) {
        video.pause();
        video.currentTime = 0;
      }
    });

    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, [currentReelIndex, reels, isMuted]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showCommentModal || showMoreMenu || showShareSheet) return;

      switch(e.key) {
        case 'ArrowUp':
          e.preventDefault();
          scrollToReel(currentReelIndex - 1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          scrollToReel(currentReelIndex + 1);
          break;
        case ' ':
          e.preventDefault();
          togglePlayPause();
          break;
        case 'm':
        case 'M':
          e.preventDefault();
          toggleMute();
          break;
        case 'l':
        case 'L':
          e.preventDefault();
          handleDoubleTap();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentReelIndex, showCommentModal, showMoreMenu, showShareSheet]);

  // Scroll to specific reel
  const scrollToReel = (index) => {
    if (index < 0 || index >= reels.length) return;
    
    isScrolling.current = true;
    setCurrentReelIndex(index);
    
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: index * window.innerHeight,
        behavior: 'smooth'
      });
    }
    
    setTimeout(() => {
      isScrolling.current = false;
    }, 300);
  };

  // Toggle play/pause
  const togglePlayPause = () => {
    if (!videoRefs.current[currentReelIndex]) return;
    
    const video = videoRefs.current[currentReelIndex];
    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  // Toggle mute
  const toggleMute = () => {
    setIsMuted(!isMuted);
    setShowMuteToast(true);
    
    if (muteToastTimeout.current) {
      clearTimeout(muteToastTimeout.current);
    }
    
    muteToastTimeout.current = setTimeout(() => {
      setShowMuteToast(false);
    }, 1000);
  };

  // Handle double tap like (Instagram style)
  const handleDoubleTap = () => {
    const currentReel = reels[currentReelIndex];
    if (!currentReel) return;

    // Visual feedback
    const likeElement = document.createElement('div');
    likeElement.innerHTML = '<div style="position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 80px; color: #ff3040; z-index: 1000; animation: heartBeat 0.5s ease-out;">❤️</div>';
    document.body.appendChild(likeElement);
    
    setTimeout(() => {
      document.body.removeChild(likeElement);
    }, 500);

    // Toggle like
    if (!likedReels.has(currentReel.promotion_id)) {
      handleLike(currentReel.promotion_id, false);
      setLikedReels(prev => new Set(prev).add(currentReel.promotion_id));
    }
  };

  // Touch handlers for mobile
  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
    touchStartX.current = e.touches[0].clientX;
    
    // Detect double tap
    const currentTime = new Date().getTime();
    const tapLength = currentTime - lastTapTime.current;
    
    if (tapLength < 300 && tapLength > 0) {
      // Double tap detected
      handleDoubleTap();
      e.preventDefault();
    }
    
    lastTapTime.current = currentTime;
    
    // Start long press for pause
    longPressTimeout.current = setTimeout(() => {
      if (videoRefs.current[currentReelIndex]) {
        videoRefs.current[currentReelIndex].pause();
        setIsPlaying(false);
      }
    }, 500);
  };

  const handleTouchEnd = (e) => {
    clearTimeout(longPressTimeout.current);
    
    const touchEndY = e.changedTouches[0].clientY;
    const touchEndX = e.changedTouches[0].clientX;
    
    const deltaY = touchEndY - touchStartY.current;
    const deltaX = touchEndX - touchStartX.current;
    
    // Vertical swipe for navigation
    if (Math.abs(deltaY) > 50 && Math.abs(deltaY) > Math.abs(deltaX)) {
      if (deltaY < 0) {
        // Swipe up - next reel
        scrollToReel(currentReelIndex + 1);
      } else {
        // Swipe down - previous reel
        scrollToReel(currentReelIndex - 1);
      }
    } else if (Math.abs(deltaX) < 10 && Math.abs(deltaY) < 10) {
      // Single tap
      if (!isPlaying) {
        // Resume playback if paused
        if (videoRefs.current[currentReelIndex]) {
          videoRefs.current[currentReelIndex].play();
          setIsPlaying(true);
        }
      }
    }
  };

  const handleTouchMove = (e) => {
    clearTimeout(longPressTimeout.current);
  };

  // Like functionality
  const handleLike = async (reelId, isLiked) => {
    if (isLiking) return;
    
    setIsLiking(true);
    try {
      if (!dealerId) {
        const currentPath = location.pathname + location.search;
        localStorage.setItem("login_url", currentPath);
        navigate("/Logout");
        return;
      }

      const formData = new FormData();
      formData.append("company_id", "2");
      formData.append("dealer_id", dealerId);
      formData.append("promotion_id", reelId);
      formData.append("product_id", "");

      const endpoint = isLiked ? "remove_promotion_like.php" : "add_promotion_like.php";
      const res = await axios.post(`${server_url}${endpoint}`, formData);

      if (res.data.status === "1") {
        setReels(prev => prev.map(item =>
          item.promotion_id === reelId ? {
            ...item,
            is_like: isLiked ? "0" : "1",
            promotion_total_likes: isLiked
              ? Math.max((parseInt(item.promotion_total_likes) || 1) - 1, 0)
              : (parseInt(item.promotion_total_likes) || 0) + 1
          } : item
        ));

        if (!isLiked) {
          setLikedReels(prev => new Set(prev).add(reelId));
        } else {
          setLikedReels(prev => {
            const newSet = new Set(prev);
            newSet.delete(reelId);
            return newSet;
          });
        }
      }
    } catch (error) {
      console.error("Failed to update like:", error);
    } finally {
      setIsLiking(false);
    }
  };

  // Share functionality
  const handleShare = async (url, title) => {
    try {
      const shareData = {
        title: title || "Check out this reel!",
        text: "Watch this amazing content!",
        url: url,
      };

      if (navigator.share && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(url);
        alert("Link copied to clipboard!");
      }
    } catch (error) {
      console.error("Share failed:", error);
      navigator.clipboard.writeText(url);
      alert("Link copied to clipboard!");
    }
  };

  // Comment functionality
  const fetchComments = async (reelId) => {
    try {
      const formData = new FormData();
      formData.append("company_id", "2");
      formData.append("dealer_id", dealerId);
      formData.append("promotion_id", reelId);
      formData.append("product_id", "");

      const res = await axios.post(`${server_url}promotion_comment_list.php`, formData);
      
      if (res.data.status === "1") {
        setComments(res.data.result);
      } else {
        setComments([]);
      }
    } catch (error) {
      console.error("Failed to load comments:", error);
      setComments([]);
    }
  };

  const openCommentModal = (reelId) => {
    setSelectedReelId(reelId);
    setShowCommentModal(true);
    fetchComments(reelId);
  };

  const handleCommentSubmit = async () => {
    if (!commentText.trim() || !selectedReelId) return;

    if (!dealerId) {
      const currentPath = location.pathname + location.search;
      localStorage.setItem("login_url", currentPath);
      navigate("/Logout");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("company_id", "2");
      formData.append("dealer_id", dealerId);
      formData.append("promotion_id", selectedReelId);
      formData.append("product_id", "");
      formData.append("promotion_comment", commentText);

      const res = await axios.post(`${server_url}add_promotion_comment.php`, formData);
      
      if (res.data.status === "1") {
        setCommentText("");
        fetchComments(selectedReelId);
        
        // Update comment count
        setReels(prev => prev.map(item =>
          item.promotion_id === selectedReelId ? {
            ...item,
            promotion_total_comment: (parseInt(item.promotion_total_comment) || 0) + 1
          } : item
        ));
      }
    } catch (error) {
      console.error("Failed to add comment:", error);
    }
  };

  const handleDeleteComment = async (commentId) => {
    setCommentToDelete(commentId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteComment = async () => {
    if (!dealerId || !selectedReelId || !commentToDelete) return;

    try {
      const formData = new FormData();
      formData.append("company_id", "2");
      formData.append("dealer_id", dealerId);
      formData.append("promotion_id", selectedReelId);
      formData.append("product_id", "");
      formData.append("promotion_comment_id", commentToDelete);

      const res = await axios.post(`${server_url}remove_promotion_comment.php`, formData);
      
      if (res.data.status === "1") {
        fetchComments(selectedReelId);
        
        // Update comment count
        setReels(prev => prev.map(item =>
          item.promotion_id === selectedReelId ? {
            ...item,
            promotion_total_comment: Math.max((parseInt(item.promotion_total_comment) || 1) - 1, 0)
          } : item
        ));
      }
    } catch (error) {
      console.error("Failed to delete comment:", error);
    } finally {
      setShowDeleteConfirm(false);
      setCommentToDelete(null);
    }
  };

  // Format numbers (K, M)
  const formatNumber = (num) => {
    if (!num) return "0";
    
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  // Render loading skeleton
  const renderSkeleton = () => (
    <div className="reel-skeleton" style={styles.skeleton}>
      <div style={styles.skeletonContent}>
        <div style={styles.skeletonVideo}></div>
        <div style={styles.skeletonSidebar}>
          <div style={styles.skeletonAction}></div>
          <div style={styles.skeletonAction}></div>
          <div style={styles.skeletonAction}></div>
          <div style={styles.skeletonAction}></div>
        </div>
        <div style={styles.skeletonInfo}>
          <div style={styles.skeletonText}></div>
          <div style={{...styles.skeletonText, width: '70%'}}></div>
        </div>
      </div>
    </div>
  );

  // Styles
  const styles = {
    container: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: '#000',
      overflow: 'hidden',
    },
    reelsContainer: {
      height: '100vh',
      overflowY: 'auto',
      scrollSnapType: 'y mandatory',
      WebkitOverflowScrolling: 'touch',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
    },
    reelItem: {
      height: '100vh',
      width: '100%',
      scrollSnapAlign: 'start',
      position: 'relative',
      backgroundColor: '#000',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    videoContainer: {
      position: 'relative',
      width: '100%',
      height: '100%',
      maxWidth: '500px',
      backgroundColor: '#000',
    },
    video: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
    },
    overlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 50%, rgba(0,0,0,0) 100%)',
    },
    header: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      padding: '16px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      zIndex: 100,
      background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
    },
    backButton: {
      background: 'rgba(0,0,0,0.5)',
      border: 'none',
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontSize: '20px',
      cursor: 'pointer',
      backdropFilter: 'blur(10px)',
    },
    reelTitle: {
      color: '#fff',
      fontSize: '16px',
      fontWeight: '600',
      flex: 1,
      marginLeft: '16px',
    },
    moreButton: {
      background: 'rgba(0,0,0,0.5)',
      border: 'none',
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontSize: '20px',
      cursor: 'pointer',
      backdropFilter: 'blur(10px)',
    },
    sidebar: {
      position: 'absolute',
      right: '16px',
      bottom: '100px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '24px',
      zIndex: 100,
    },
    actionButton: {
      background: 'transparent',
      border: 'none',
      color: '#fff',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '8px',
      cursor: 'pointer',
      padding: '8px',
      borderRadius: '8px',
      transition: 'background 0.2s',
    },
    actionButtonHover: {
      background: 'rgba(255,255,255,0.1)',
    },
    actionIcon: {
      fontSize: '28px',
      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
    },
    actionCount: {
      fontSize: '13px',
      fontWeight: '600',
      color: '#fff',
      textShadow: '0 1px 2px rgba(0,0,0,0.5)',
    },
    userInfo: {
      position: 'absolute',
      left: '16px',
      bottom: '100px',
      color: '#fff',
      zIndex: 100,
      maxWidth: 'calc(100% - 120px)',
    },
    username: {
      fontSize: '16px',
      fontWeight: '600',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    followButton: {
      background: '#0095f6',
      color: '#fff',
      border: 'none',
      padding: '4px 12px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '600',
      cursor: 'pointer',
    },
    caption: {
      fontSize: '14px',
      lineHeight: '1.4',
      marginBottom: '8px',
      maxHeight: '60px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
    },
    musicInfo: {
      fontSize: '14px',
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginTop: '8px',
    },
    progressBar: {
      position: 'absolute',
      top: '0',
      left: '0',
      right: '0',
      height: '2px',
      backgroundColor: 'rgba(255,255,255,0.3)',
      zIndex: 1000,
    },
    progressFill: {
      height: '100%',
      backgroundColor: '#fff',
      width: `${progress}%`,
      transition: 'width 0.1s linear',
    },
    muteButton: {
      position: 'absolute',
      right: '16px',
      top: '80px',
      background: 'rgba(0,0,0,0.5)',
      border: 'none',
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontSize: '20px',
      cursor: 'pointer',
      backdropFilter: 'blur(10px)',
      zIndex: 100,
    },
    playPauseButton: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      background: 'rgba(0,0,0,0.5)',
      border: 'none',
      width: '60px',
      height: '60px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontSize: '30px',
      cursor: 'pointer',
      opacity: isPlaying ? 0 : 1,
      transition: 'opacity 0.3s',
      zIndex: 100,
    },
    muteToast: {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      background: 'rgba(0,0,0,0.8)',
      color: '#fff',
      padding: '12px 24px',
      borderRadius: '24px',
      fontSize: '14px',
      fontWeight: '600',
      zIndex: 2000,
      opacity: showMuteToast ? 1 : 0,
      transition: 'opacity 0.3s',
      pointerEvents: 'none',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    doubleTapHeart: {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      fontSize: '100px',
      color: '#fff',
      opacity: 0,
      zIndex: 2000,
      animation: 'heartAnimation 0.8s ease-out',
    },
    commentModal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: '#fff',
      zIndex: 2000,
      display: 'flex',
      flexDirection: 'column',
    },
    commentHeader: {
      padding: '16px',
      borderBottom: '1px solid #dbdbdb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    commentTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#262626',
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '24px',
      color: '#262626',
      cursor: 'pointer',
      padding: '8px',
    },
    commentList: {
      flex: 1,
      overflowY: 'auto',
      padding: '16px',
    },
    commentItem: {
      display: 'flex',
      gap: '12px',
      marginBottom: '16px',
    },
    commentAvatar: {
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      backgroundColor: '#dbdbdb',
      flexShrink: 0,
    },
    commentContent: {
      flex: 1,
    },
    commentUsername: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#262626',
      marginBottom: '4px',
    },
    commentText: {
      fontSize: '14px',
      color: '#262626',
      lineHeight: 1.4,
    },
    commentTime: {
      fontSize: '12px',
      color: '#8e8e8e',
      marginTop: '4px',
    },
    commentDelete: {
      background: 'none',
      border: 'none',
      color: '#ed4956',
      fontSize: '12px',
      cursor: 'pointer',
      padding: '4px',
    },
    commentInputContainer: {
      padding: '16px',
      borderTop: '1px solid #dbdbdb',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    commentInput: {
      flex: 1,
      border: 'none',
      outline: 'none',
      fontSize: '14px',
      color: '#262626',
      padding: '12px 0',
    },
    commentSendButton: {
      background: 'none',
      border: 'none',
      color: '#0095f6',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      padding: '8px',
      opacity: commentText.trim() ? 1 : 0.3,
    },
    deleteConfirmModal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      zIndex: 3000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    deleteConfirmContent: {
      backgroundColor: '#fff',
      borderRadius: '12px',
      width: '90%',
      maxWidth: '400px',
      overflow: 'hidden',
    },
    deleteConfirmTitle: {
      padding: '24px',
      textAlign: 'center',
      fontSize: '18px',
      fontWeight: '600',
      color: '#262626',
      borderBottom: '1px solid #dbdbdb',
    },
    deleteConfirmButtons: {
      display: 'flex',
      flexDirection: 'column',
    },
    deleteConfirmButton: {
      padding: '16px',
      border: 'none',
      background: 'none',
      fontSize: '16px',
      cursor: 'pointer',
      borderBottom: '1px solid #dbdbdb',
    },
    deleteButton: {
      color: '#ed4956',
      fontWeight: '600',
    },
    cancelButton: {
      color: '#262626',
    },
    loadingSpinner: {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '50px',
      height: '50px',
      border: '3px solid rgba(255,255,255,0.3)',
      borderTop: '3px solid #fff',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      zIndex: 1000,
    },
    skeleton: {
      height: '100vh',
      width: '100%',
      backgroundColor: '#1a1a1a',
      position: 'relative',
    },
    skeletonContent: {
      position: 'relative',
      width: '100%',
      height: '100%',
      maxWidth: '500px',
      margin: '0 auto',
    },
    skeletonVideo: {
      width: '100%',
      height: '100%',
      backgroundColor: '#2a2a2a',
    },
    skeletonSidebar: {
      position: 'absolute',
      right: '16px',
      bottom: '100px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '24px',
    },
    skeletonAction: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      backgroundColor: '#2a2a2a',
    },
    skeletonInfo: {
      position: 'absolute',
      left: '16px',
      bottom: '100px',
      right: '120px',
    },
    skeletonText: {
      height: '16px',
      backgroundColor: '#2a2a2a',
      borderRadius: '8px',
      marginBottom: '8px',
      width: '50%',
    },
  };

  // Add CSS animations
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.innerHTML = `
      @keyframes spin {
        0% { transform: translate(-50%, -50%) rotate(0deg); }
        100% { transform: translate(-50%, -50%) rotate(360deg); }
      }
      @keyframes heartAnimation {
        0% { opacity: 0; transform: translate(-50%, -50%) scale(0.3); }
        50% { opacity: 1; transform: translate(-50%, -50%) scale(1.2); }
        100% { opacity: 0; transform: translate(-50%, -50%) scale(1); }
      }
      .reels-container::-webkit-scrollbar {
        display: none;
      }
    `;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);

  return (
    <div style={styles.container}>
      {/* Progress bar */}
      <div style={styles.progressBar}>
        <div style={styles.progressFill}></div>
      </div>

      {/* Header */}
      <div style={styles.header}>
        <button onClick={() => navigate(-1)} style={styles.backButton}>
          <FaArrowLeft />
        </button>
        <div style={styles.reelTitle}>Promotional Videos</div>
       
      </div>

      {/* Reels container */}
      <div 
        ref={scrollContainerRef} 
        style={styles.reelsContainer}
        className="reels-container"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
      >
        {reels.map((reel, index) => (
          <div key={reel.promotion_id} style={styles.reelItem}>
            <div style={styles.videoContainer}>
              {reel.promotion_type === "2" ? (
                <>
                  <video
                    ref={el => videoRefs.current[index] = el}
                    style={styles.video}
                    src={reel.promotion_attechment}
                    loop
                    playsInline
                    muted={isMuted}
                    poster={reel.promotion_video_thumbnail}
                    onWaiting={() => setBuffering(true)}
                    onPlaying={() => setBuffering(false)}
                    onCanPlay={() => setBuffering(false)}
                  />
                  {buffering && index === currentReelIndex && (
                    <div style={styles.loadingSpinner}></div>
                  )}
                </>
              ) : (
                <img
                  src={reel.promotion_attechment}
                  alt={reel.promotion_title}
                  style={styles.video}
                />
              )}
              
              <div style={styles.overlay}></div>
              
              {/* Play/Pause button */}
              <button 
                onClick={togglePlayPause} 
                style={styles.playPauseButton}
              >
                {isPlaying ? <MdPause /> : <FaPlay />}
              </button>
              
              {/* Mute button */}
              {index === currentReelIndex && (
                <button onClick={toggleMute} style={styles.muteButton}>
                  {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
                </button>
              )}
            </div>

            

            {/* Sidebar actions */}
            <div style={styles.sidebar}>
              <button 
                onClick={() => handleLike(reel.promotion_id, reel.is_like === "1")}
                style={{
                  ...styles.actionButton,
                  ...(likedReels.has(reel.promotion_id) || reel.is_like === "1" ? { color: '#ff3040' } : {})
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                {likedReels.has(reel.promotion_id) || reel.is_like === "1" ? (
                  <FaHeart style={styles.actionIcon} />
                ) : (
                  <FiHeart style={styles.actionIcon} />
                )}
                <span style={styles.actionCount}>
                  {formatNumber(reel.promotion_total_likes)}
                </span>
              </button>

              <button 
                onClick={() => openCommentModal(reel.promotion_id)}
                style={styles.actionButton}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <FiMessageCircle style={styles.actionIcon} />
                <span style={styles.actionCount}>
                  {formatNumber(reel.promotion_total_comment)}
                </span>
              </button>

              <button 
                onClick={() => handleShare(reel.promotion_attechment, reel.promotion_title)}
                style={styles.actionButton}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <FiSend style={styles.actionIcon} />
                <span style={styles.actionCount}>Share</span>
              </button>

             
            </div>
          </div>
        ))}

        {/* Loading skeleton */}
        {loading && reels.length === 0 && renderSkeleton()}
        
        {/* Load more indicator */}
        {loading && reels.length > 0 && (
          <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={styles.loadingSpinner}></div>
          </div>
        )}
      </div>

      {/* Mute/Unmute Toast */}
      <div style={styles.muteToast}>
        {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
        {isMuted ? "Muted" : "Unmuted"}
      </div>

      {/* Comment Modal */}
      {showCommentModal && (
        <div style={styles.commentModal}>
          <div style={styles.commentHeader}>
            <div style={styles.commentTitle}>Comments</div>
            <button 
              onClick={() => setShowCommentModal(false)} 
              style={styles.closeButton}
            >
              <MdClose />
            </button>
          </div>
          
          <div style={styles.commentList}>
            {comments.length > 0 ? (
              comments.map(comment => (
                <div key={comment.promotion_comment_id} style={styles.commentItem}>
                  <div style={styles.commentAvatar}></div>
                  <div style={styles.commentContent}>
                    <div style={styles.commentUsername}>
                      {comment.dealer_firm_name}
                    </div>
                    <div style={styles.commentText}>
                      {comment.promotion_comment}
                    </div>
                    <div style={styles.commentTime}>
                      {comment.c_date}
                    </div>
                    {comment.promotion_comment_by === loginId && (
                      <button 
                        onClick={() => handleDeleteComment(comment.promotion_comment_id)}
                        style={styles.commentDelete}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#8e8e8e' }}>
                No comments yet. Be the first to comment!
              </div>
            )}
          </div>

          <div style={styles.commentInputContainer}>
            <input
              type="text"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Add a comment..."
              style={styles.commentInput}
              onKeyPress={(e) => e.key === 'Enter' && handleCommentSubmit()}
            />
            <button
              onClick={handleCommentSubmit}
              style={styles.commentSendButton}
              disabled={!commentText.trim()}
            >
              Post
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div style={styles.deleteConfirmModal}>
          <div style={styles.deleteConfirmContent}>
            <div style={styles.deleteConfirmTitle}>
              Delete Comment?
            </div>
            <div style={styles.deleteConfirmButtons}>
              <button 
                onClick={confirmDeleteComment}
                style={{...styles.deleteConfirmButton, ...styles.deleteButton}}
              >
                Delete
              </button>
              <button 
                onClick={() => setShowDeleteConfirm(false)}
                style={{...styles.deleteConfirmButton, ...styles.cancelButton}}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InstagramReels;